import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { components } from '@tabservice/tabfile-client';
import { Plus, Webhook as WebhookIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Field, Input } from '@/components/ui/field';
import { Checkbox } from '@/components/ui/toggles';
import { SecretOnce } from '@/components/ui/secret';
import { EmptyState, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { Table, TableWrap, TBody, TD, TH, THead, TR, TablePagination} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { queryKeys, webhookDeliveriesPageQuery, webhooksPageQuery } from '@/lib/queries';
import { tabfile, errorMessage } from '@/lib/api';
import { formatDate, formatDateTimeShort } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

type Webhook = components['schemas']['Webhook'];
type WebhookCreated = components['schemas']['WebhookCreated'];
type WebhookEvent = components['schemas']['WebhookEvent'];

/** The events a customer can subscribe to, with what each one means to them. */
const EVENTS: { value: WebhookEvent; label: string; hint: string }[] = [
  { value: 'upload.checked', label: 'Upload checked', hint: 'A file has been read and validated.' },
  { value: 'efile.transmitted', label: 'E-file transmitted', hint: 'A transmission reached the IRS.' },
  { value: 'efile.acknowledged', label: 'E-file acknowledged', hint: 'The IRS has answered.' },
  { value: 'efile.failed', label: 'E-file failed', hint: 'An e-file could not be sent.' },
];

const statusTone = { healthy: 'success', failing: 'warning', disabled: 'neutral' } as const;
const statusLabel = { healthy: 'Healthy', failing: 'Failing', disabled: 'Disabled' } as const;

/**
 * Where a customer's systems are told what happened, without polling. Every endpoint has a
 * signing secret shown once; the Developers guide says how to verify it.
 */
export function WebhooksTab({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  const webhooks = useQuery(webhooksPageQuery(page, PAGE_SIZE));

  const [createOpen, setCreateOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [created, setCreated] = useState<WebhookCreated | null>(null);
  const [removing, setRemoving] = useState<Webhook | null>(null);
  const [deliveriesFor, setDeliveriesFor] = useState<Webhook | null>(null);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: queryKeys.webhooks });

  const create = useMutation({
    mutationFn: () =>
      tabfile.POST('/v1/webhooks', { body: { url: url.trim(), ...(events.length > 0 ? { events } : {}) } }),
    onSuccess: (result) => {
      setCreated(result);
      setUrl('');
      setEvents([]);
      refresh();
    },
    onError: (error) => toast.error('Could not add the endpoint', errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (webhookId: string) => tabfile.DELETE('/v1/webhooks/{webhookId}', { params: { path: { webhookId } } }),
    onSuccess: () => {
      toast.success('Endpoint removed', 'Nothing more is sent to it.');
      setRemoving(null);
      refresh();
    },
    onError: (error) => toast.error('Could not remove the endpoint', errorMessage(error)),
  });

  const test = useMutation({
    mutationFn: (webhookId: string) => tabfile.POST('/v1/webhooks/{webhookId}/test', { params: { path: { webhookId } } }),
    onSuccess: () => toast.success('Test event queued', 'A signed "test" event is on its way. Check the deliveries in a minute.'),
    onError: (error) => toast.error('Could not send a test', errorMessage(error)),
  });

  const items = webhooks.data?.items ?? [];
  const validUrl = /^https:\/\/\S+$/i.test(url.trim());

  return (
    <Card data-tour="webhooks">
      <SectionHeader
        title="Webhooks"
        description="Tab Service posts signed events to these HTTPS endpoints so your systems do not have to poll."
        actions={
          isOwner ? (
            <Button
              onClick={() => {
                setCreated(null);
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              Add endpoint
            </Button>
          ) : null
        }
      />

      {webhooks.isPending ? (
        <TableSkeleton rows={2} columns={4} />
      ) : items.length === 0 ? (
        <EmptyState
          icon={WebhookIcon}
          title="No endpoints yet"
          description={
            isOwner
              ? 'Add an HTTPS endpoint to be told when files are checked and when the IRS answers.'
              : 'An owner can add an HTTPS endpoint to be told when the IRS answers.'
          }
          action={
            isOwner ? (
              <Button
                onClick={() => {
                  setCreated(null);
                  setCreateOpen(true);
                }}
              >
                Add endpoint
              </Button>
            ) : undefined
          }
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>Endpoint</TH>
                  <TH>Events</TH>
                  <TH>Status</TH>
                  <TH>Added</TH>
                  <TH aria-label="Actions" />
                </tr>
              </THead>
              <TBody>
                {items.map((item) => (
                  <TR key={item.id}>
                    <TD className="max-w-[360px] truncate font-mono text-sm" title={item.url}>
                      {item.url}
                    </TD>
                    <TD className="text-muted-foreground">
                      {item.events.length === 0 ? 'All events' : item.events.map((event) => EVENTS.find((e) => e.value === event)?.label ?? event).join(', ')}
                    </TD>
                    <TD>
                      <Pill tone={statusTone[item.status]}>{statusLabel[item.status]}</Pill>
                    </TD>
                    <TD className="whitespace-nowrap text-muted-foreground">{formatDate(item.createdAt)}</TD>
                    <TD className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => setDeliveriesFor(item)}>
                        Deliveries
                      </Button>
                      {isOwner ? (
                        <>
                          <Button variant="ghost" size="sm" disabled={test.isPending} onClick={() => test.mutate(item.id)}>
                            Send test
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => setRemoving(item)}>
                            Remove
                          </Button>
                        </>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={webhooks.data?.total ?? webhooks.data?.items?.length ?? 0}
            busy={webhooks.isFetching}
            onPageChange={setPage}
          />
        </>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent
          title={created ? 'Endpoint added' : 'Add a webhook endpoint'}
          description={
            created
              ? 'The signing secret is shown once. Verify every delivery with it.'
              : 'An HTTPS address your system listens on. Choose the events, or leave them all selected.'
          }
          footer={
            created ? (
              <DialogClose asChild>
                <Button variant="primary">Done</Button>
              </DialogClose>
            ) : (
              <>
                <DialogClose asChild>
                  <Button variant="outline">Cancel</Button>
                </DialogClose>
                <Button variant="primary" disabled={!validUrl || create.isPending} onClick={() => create.mutate()}>
                  {create.isPending ? <Spinner /> : null}
                  Add endpoint
                </Button>
              </>
            )
          }
        >
          {created ? (
            <SecretOnce
              label="Signing secret"
              value={created.secret}
              note="Deliveries carry X-TabService-Signature: t=<timestamp>,v1=<hmac>. Compute HMAC-SHA256 of <timestamp>.<body> with this secret and compare."
            />
          ) : (
            <div className="flex flex-col gap-4">
              <Field label="Endpoint URL" hint="HTTPS only. Tab Service sends a POST with a JSON body.">
                {(props) => (
                  <Input
                    {...props}
                    value={url}
                    autoFocus
                    onChange={(event) => setUrl(event.target.value)}
                    placeholder="https://example.com/tab-service/events"
                  />
                )}
              </Field>
              <fieldset className="flex flex-col gap-2">
                <legend className="mb-1 text-base font-medium text-gray-3">Events</legend>
                <p className="text-sm text-muted-foreground">Leave every box clear to receive all events.</p>
                {EVENTS.map((event) => (
                  <label
                    key={event.value}
                    className="flex cursor-pointer items-start gap-2.5 rounded-md px-1.5 py-1 transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]"
                  >
                    <Checkbox
                      checked={events.includes(event.value)}
                      label={event.label}
                      onCheckedChange={(next) =>
                        setEvents((current) => (next ? [...current, event.value] : current.filter((value) => value !== event.value)))
                      }
                    />
                    <span>
                      <span className="block text-base text-gray-3">{event.label}</span>
                      <span className="block text-sm text-muted-foreground">{event.hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this endpoint"
        description={removing?.url}
        body={<p className="text-base text-muted-foreground">Nothing more is sent to it. Deliveries already queued are dropped.</p>}
        confirmLabel="Remove endpoint"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
      />

      {deliveriesFor ? <DeliveriesDialog webhook={deliveriesFor} onClose={() => setDeliveriesFor(null)} /> : null}
    </Card>
  );
}

function DeliveriesDialog({ webhook, onClose }: { webhook: Webhook; onClose: () => void }) {
  const [page, setPage] = useState(1);
  const deliveries = useQuery({
    ...webhookDeliveriesPageQuery(webhook.id, page, PAGE_SIZE, true),
    refetchInterval: 10_000,
  });
  const items = deliveries.data?.items ?? [];
  const tone = { pending: 'info', delivered: 'success', failed: 'danger' } as const;

  return (
    <Dialog open onOpenChange={(open) => (open ? undefined : onClose())}>
      <DialogContent
        title="Recent deliveries"
        description={webhook.url}
        footer={
          <DialogClose asChild>
            <Button variant="outline">Close</Button>
          </DialogClose>
        }
      >
        {deliveries.isPending ? (
          <TableSkeleton rows={3} columns={4} />
        ) : items.length === 0 ? (
          <p className="text-base text-muted-foreground">Nothing has been sent to this endpoint yet.</p>
        ) : (
          <>
            <TableWrap>
              <Table>
                <THead>
                  <tr>
                    <TH>Event</TH>
                    <TH>Status</TH>
                    <TH align="right">Attempts</TH>
                    <TH>Response</TH>
                    <TH>When</TH>
                  </tr>
                </THead>
                <TBody>
                  {items.map((item) => (
                    <TR key={item.id}>
                      <TD className="font-mono text-sm">{item.event}</TD>
                      <TD>
                        <Pill tone={tone[item.status]}>{item.status}</Pill>
                      </TD>
                      <TD numeric>{item.attempts}</TD>
                      <TD className="text-muted-foreground">{item.lastHttpStatus ?? '—'}</TD>
                      <TD className="whitespace-nowrap text-muted-foreground">{formatDateTimeShort(item.createdAt)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
            <TablePagination
              page={page}
              pageSize={PAGE_SIZE}
              total={deliveries.data?.total ?? items.length}
              busy={deliveries.isFetching}
              onPageChange={setPage}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
